import type { NotificationRow, NotificationsInbox } from '@/lib/types';

/**
 * إشعارُ سطح المكتب لتنبيهٍ وصل والتبويبُ مفتوح (25 أكتوبر 2026 — «أبي الإشعارات الديسكتوب حقها تكون فعالة»).
 *
 * إشعارُ الدفع (`sw.js`) يصل والتطبيقُ مغلق. أما والتبويبُ مفتوحٌ — في نافذةٍ خلف غيرها، أو مصغّرٌ على الحاسوب — فكانت
 * القشرةُ تحدّث عدّادَ غير المقروء كلَّ دقيقة **ولا تقول شيئاً**؛ فعلى الحاسوب الذي لا يثبَّت عليه التطبيق يفوت التنبيه حتى
 * يعود صاحبُه إلى التبويب. الآن كلُّ جلبٍ للعدّاد يمرّ بـ`detectArrival`: تنبيهٌ أحدثُ من آخر ما رُئي = إشعارُ نظامٍ من
 * عامل الخدمة (الإذنُ ممنوح) حين لا يكون التبويبُ ظاهراً، وبلاغٌ داخل الصفحة حين يكون ظاهراً.
 *
 * الحسابُ خالصٌ (`detectArrival`) فيُختبر بلا متصفّح؛ وأولُ جلبٍ بعد الدخول **خطُّ أساس** لا إشعار — وإلا رنّ الجهازُ
 * بكل قديمٍ لم يُقرأ عند كل فتح.
 */
export interface ArrivalState { latestId: string | null; latestAt: string | null }

export const EMPTY_ARRIVAL: ArrivalState = { latestId: null, latestAt: null };

/** ما وصل جديداً منذ آخر حالة — `null` إن لم يصل شيء (أو كانت الحالةُ خطَّ أساسٍ لم يُثبَّت بعد) */
export function detectArrival(prev: ArrivalState | undefined, inbox: NotificationsInbox): { next: ArrivalState; arrived: NotificationRow | null } {
  const top = inbox.items[0] ?? null;
  const next: ArrivalState = { latestId: top?.id ?? prev?.latestId ?? null, latestAt: top?.createdAt ?? prev?.latestAt ?? null };
  if (!prev) return { next, arrived: null }; // خطُّ الأساس: أولُ جلب
  if (!top || top.id === prev.latestId) return { next, arrived: null };
  // أحدثُ مما رأينا — بالتاريخ لا بالمعرّف وحده: صفٌّ أقدم يصعد حين يُقرأ ما فوقه ليس وصولاً (ويبقى أحدثُ وقتٍ رأيناه هو الحدّ)
  if (prev.latestAt && top.createdAt <= prev.latestAt) return { next: { latestId: top.id, latestAt: prev.latestAt }, arrived: null };
  return { next, arrived: top };
}

export function desktopNotifyAllowed(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

/** إشعارُ النظام من عامل الخدمة (يُغلق بالنقر ويفتح وجهتَه) — وإلا `new Notification` حيث لا عامل */
export async function showDesktopNotification(n: NotificationRow): Promise<boolean> {
  if (!desktopNotifyAllowed()) return false;
  const url = n.url && n.url.startsWith('/') && !n.url.startsWith('//') ? n.url : `/me/notifications?focus=${encodeURIComponent(n.id)}`;
  const options: NotificationOptions = { body: n.bodyAr, icon: '/brand/icon-192.png', badge: '/brand/badge-96.png', lang: 'ar', dir: 'rtl', tag: `rafrafa-${n.id}`, data: { url } };
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) { await reg.showNotification(n.titleAr, options); return true; }
    }
    const note = new Notification(n.titleAr, options);
    note.onclick = () => { window.focus(); window.location.assign(url); note.close(); };
    return true;
  } catch { return false; }
}
